import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Upload, CheckCircle, AlertCircle, Copy, Check, RotateCcw } from "lucide-react";
import axios from "axios";
import { useAppConfig } from "@/contexts/AppConfigContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicUpload() {
  const { appName, logoB64 } = useAppConfig();
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [state, setState] = useState("idle"); // idle | uploading | extracted | done_dev
  const [extractedData, setExtractedData] = useState({});
  const [pagesProcessed, setPagesProcessed] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [error, setError] = useState("");

  // Revoke object URL when it changes or on unmount
  useEffect(() => {
    return () => { if (fileUrl) URL.revokeObjectURL(fileUrl); };
  }, [fileUrl]);

  useEffect(() => {
    axios.get(`${API_URL}/public/requests/${token}`)
      .then(r => setRequest(r.data))
      .catch(() => setError("Request not found or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFileUrl(URL.createObjectURL(f));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a file"); return; }
    setState("uploading");
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axios.post(`${API_URL}/public/requests/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (request?.link_type === "production") {
        setExtractedData(resp.data.extracted_fields || {});
        setPagesProcessed(resp.data.pages_processed || 0);
        setState("extracted");
      } else {
        setState("done_dev");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed. Please try again.");
      setState("idle");
    }
  };

  const copyField = (fieldName) => {
    const value = String(extractedData[fieldName] ?? "");
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    });
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const resetForm = () => {
    setFile(null);
    setFileUrl(null);
    setState("idle");
    setExtractedData({});
    setError("");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="w-8 h-8 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error && !request) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md w-full text-center shadow-sm">
        <AlertCircle className="text-red-400 mx-auto mb-3" size={32} />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Request Not Found</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  // Dev link already used — thank you screen
  if (request?.link_type !== "production" && (state === "done_dev" || request?.status === "uploaded")) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md w-full text-center shadow-sm">
        <div className="w-12 h-12 bg-[#E5F0F2] rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-[#006080]" size={24} />
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">Document Uploaded</h2>
        <p className="text-sm text-slate-500">Your document has been received and is being processed. You can close this window.</p>
      </div>
    </div>
  );

  // Production link — two-pane extracted view
  if (state === "extracted") return (
    <div className="h-screen flex flex-col bg-[#F9FAFB] overflow-hidden" data-testid="extracted-view">
      {/* Header bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 bg-[#006080] rounded-md flex items-center justify-center flex-shrink-0">
            <FileText size={14} className="text-white" />
          </div>
          <span className="font-bold text-slate-900 text-sm flex-shrink-0">{appName}</span>
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full flex-shrink-0">
            Production
          </span>
          <span className="hidden sm:block text-sm text-slate-400 truncate ml-1">{request?.title}</span>
        </div>
        <button
          data-testid="submit-new-document-btn"
          onClick={resetForm}
          className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-[#006080] text-[#006080] rounded-md hover:bg-[#E5F0F2] transition-colors flex-shrink-0 ml-3"
        >
          <RotateCcw size={12} />
          Submit Another
        </button>
      </div>

      {/* Two-pane body */}
      <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
        {/* Left pane: document preview */}
        <div className="flex-1 bg-slate-100 border-b sm:border-b-0 sm:border-r border-slate-200 overflow-hidden relative" style={{ minHeight: "200px" }}>
          {fileUrl ? (
            file?.type?.startsWith("image/") ? (
              <img
                src={fileUrl}
                alt="Uploaded document"
                className="w-full h-full object-contain p-4"
                data-testid="doc-preview-image"
              />
            ) : (
              <iframe
                src={fileUrl}
                className="w-full h-full border-none"
                title="Document Preview"
                data-testid="doc-preview-iframe"
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-400">
                <FileText size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Document preview not available</p>
              </div>
            </div>
          )}
        </div>

        {/* Right pane: extracted fields */}
        <div className="w-full sm:w-80 lg:w-96 bg-white overflow-y-auto flex-shrink-0 flex flex-col">
          {/* Success header */}
          <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-emerald-500" />
              </div>
              <span className="text-sm font-bold text-slate-900">Extraction Complete</span>
            </div>
            <p className="text-xs text-slate-400 ml-9">
              {pagesProcessed} page{pagesProcessed !== 1 ? "s" : ""} processed
              {file?.name && <span className="truncate"> · {file.name}</span>}
            </p>
          </div>

          {/* Fields */}
          <div className="flex-1 p-4 space-y-2">
            {request?.schema?.fields?.length > 0 ? (
              request.schema.fields.map(field => {
                const val = extractedData[field.name];
                const hasValue = val !== undefined && val !== null && val !== "";
                const displayVal = hasValue
                  ? (Array.isArray(val) ? val.join(", ") : String(val))
                  : null;
                return (
                  <div
                    key={field.name}
                    data-testid={`extracted-field-${field.name}`}
                    className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 leading-none">
                        {field.label}
                      </p>
                      <p className={`text-sm font-medium break-words leading-snug ${displayVal ? "text-slate-800" : "text-slate-400 font-normal italic"}`}>
                        {displayVal ?? "Not found"}
                      </p>
                    </div>
                    {displayVal && (
                      <button
                        onClick={() => copyField(field.name)}
                        data-testid={`copy-field-${field.name}`}
                        className="p-1.5 hover:bg-slate-200 rounded flex-shrink-0 transition-colors mt-0.5"
                        title={`Copy ${field.label}`}
                      >
                        {copiedField === field.name
                          ? <Check size={13} className="text-emerald-500" />
                          : <Copy size={13} className="text-slate-400" />}
                      </button>
                    )}
                  </div>
                );
              })
            ) : Object.keys(extractedData).length > 0 ? (
              Object.entries(extractedData).map(([k, v]) => {
                const hasValue = v !== undefined && v !== null && v !== "";
                return (
                  <div key={k} data-testid={`extracted-field-${k}`}
                    className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{k.replace(/_/g, " ")}</p>
                      <p className="text-sm font-medium text-slate-800 break-words">{hasValue ? String(v) : "Not found"}</p>
                    </div>
                    {hasValue && (
                      <button onClick={() => copyField(k)} data-testid={`copy-field-${k}`}
                        className="p-1.5 hover:bg-slate-200 rounded flex-shrink-0 transition-colors mt-0.5">
                        {copiedField === k
                          ? <Check size={13} className="text-emerald-500" />
                          : <Copy size={13} className="text-slate-400" />}
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-10 px-3">
                {request?.schema_missing ? (
                  <>
                    <AlertCircle size={20} className="text-amber-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 font-medium">Schema not available</p>
                    <p className="text-xs text-slate-400 mt-1">The extraction schema linked to this request was removed. Contact the document owner.</p>
                  </>
                ) : !request?.schema_id ? (
                  <>
                    <p className="text-sm text-slate-400">Document processed successfully.</p>
                    <p className="text-xs text-slate-400 mt-1">No extraction schema configured for this request.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-400">Document processed successfully.</p>
                    <p className="text-xs text-slate-400 mt-1">No fields could be extracted from this document.</p>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-slate-100 flex-shrink-0">
            <p className="text-[10px] text-slate-300 text-center">Secure upload · Powered by {appName}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Upload form (idle + uploading states)
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md w-full shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#006080] rounded-md flex items-center justify-center">
            <FileText size={16} className="text-white" />
          </div>
          <span className="font-bold text-slate-900">{appName}</span>
          {request?.link_type === "production" && (
            <span className="ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
              Production
            </span>
          )}
        </div>

        <h2 className="text-xl font-bold text-slate-900 mb-1">{request?.title}</h2>
        {request?.description && <p className="text-sm text-slate-500 mb-3">{request.description}</p>}

        {request?.link_type === "production" && (
          <div className="flex items-start gap-2 p-2.5 bg-[#E5F0F2] border border-[#006080]/20 rounded-md mb-4">
            <CheckCircle size={13} className="text-[#006080] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#006080]">
              Results will appear instantly after upload — no approval required.
            </p>
          </div>
        )}

        {error && (
          <div data-testid="public-upload-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={14} className="flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-4">
          <div
            data-testid="public-file-drop"
            onClick={() => state === "idle" && document.getElementById("public-file-input").click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              state === "idle"
                ? "cursor-pointer hover:border-[#006080] hover:bg-[#E5F0F2]/30 border-slate-200"
                : "border-slate-100 bg-slate-50"
            }`}
          >
            <input
              id="public-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={handleFileChange}
              className="hidden"
              data-testid="public-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={18} className="text-[#006080]" />
                <span className="text-sm font-medium text-slate-900">{file.name}</span>
              </div>
            ) : (
              <div>
                <Upload size={24} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Click to select your document</p>
                <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, WEBP · Max 10MB</p>
              </div>
            )}
          </div>

          <button
            data-testid="public-upload-submit"
            type="submit"
            disabled={!file || state === "uploading"}
            className="w-full h-10 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
          >
            {state === "uploading" ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {request?.link_type === "production" ? "Processing document..." : "Uploading..."}
              </span>
            ) : "Submit Document"}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-4">Secure upload · Powered by {appName}</p>
      </div>
    </div>
  );
}
